import type { Color, PaletteOptions } from '@mui/material';

type CustomPalette = Partial<Color> & {
  primary?: string;
  secondary?: string;
  main?: string;
  light?: string;
  hover?: string;
  lightNew?: string;
  dark?: string;
  darkNew?: string;
  contrastText?: string;
  '100New'?: string;
  150?: string;
  250?: string;
  350?: string;
  '400New'?: string;
  450?: string;
  '500New'?: string;
  550?: string;
  650?: string;
  750?: string;
  850?: string;
  950?: string;
};

// Update the Typography's variant prop options
declare module '@mui/material' {
  interface Palette {
    beige: CustomPalette;
    amber: CustomPalette;
    softAmber: CustomPalette;
    tableRow: CustomPalette;
    border: CustomPalette;
    button: CustomPalette;
    black: CustomPalette;
    blue: CustomPalette;
    purple: CustomPalette;
    magenta: CustomPalette;
    orange: CustomPalette;
    indigo: CustomPalette;
    red: CustomPalette;
    yellow: CustomPalette;
    green: CustomPalette;
    neutral: CustomPalette;
    neutralGrey: CustomPalette;
    midnightGrey: CustomPalette;
    coolGrey: CustomPalette;
    generic: CustomPalette;
  }

  interface PaletteOptions {
    beige: Palette['beige'];
    amber: Palette['amber'];
    softAmber: Palette['softAmber'];
    tableRow: Palette['tableRow'];
    border: Palette['border'];
    button: Palette['button'];
    black: Palette['black'];
    blue: Palette['blue'];
    purple: Palette['purple'];
    magenta: Palette['magenta'];
    orange: Palette['orange'];
    indigo: Palette['indigo'];
    red: Palette['red'];
    yellow: Palette['yellow'];
    green: Palette['green'];
    neutral: Palette['neutral'];
    neutralGrey: Palette['neutralGrey'];
    midnightGrey: Palette['midnightGrey'];
    coolGrey: Palette['coolGrey'];
    generic: Palette['generic'];
  }
}

// Design tokens extracted from House Plant Design System
const palette = {
  primary: {
    main: '#080A2D', // blue-950
    light: '#FFF',
    dark: '#FFF',
    contrastText: '#000',
  },
  secondary: {
    main: '#3DB157', // green-500
    light: 'rgba(61, 177, 87, 0.1)',
    dark: 'rgba(61, 177, 87, 0.2)',
  },
  text: {
    primary: '#1D1D1D', // neutral-grey-950
    secondary: '#777777',
    disabled: '#8D8D8D',
  },
  button: {
    hover: 'rgba(8, 10, 45, 0.12)',
  },
  action: {
    disabledBackground: 'rgba(8, 10, 45, 0.04)',
    hover: 'rgb(242 249 243)',
  },
  error: {
    main: '#FF3030',
    dark: '#DB3429',
    // mainNew: '#EC463D', // Design system value (red-500)
  },
  grey: {
    50: '#080A2D14',
    100: '#1D1D1D',
    200: '#858693',
    400: '#888888',
    500: '#6D6D6D',
    600: '#5D5D5D',
    700: '#4F4F4F',
    800: '#080A2D1F',
    900: '#202127',
  },
  // Green scale
  green: {
    50: '#EFFDED',
    100: '#DFFBDA', // Aligned with design system
    200: '#BAF7B6',
    300: '#8CE78F',
    400: '#69D076',
    500: '#3DB157', // Base - Aligned with design system
    600: '#2C984F',
    700: '#1E7F46',
    800: '#13663E',
    900: '#0B5438', // Aligned with design system
    950: '#0B2813',
  },
  // Blue scale
  blue: {
    50: '#F1F1FF',
    100: '#E6E5FF',
    200: '#D7D7FF',
    300: '#AEAAFE',
    400: '#887EFC',
    500: '#6350F8',
    600: '#5033EE',
    700: '#4324D7',
    800: '#371EB0',
    900: '#2E1B8C',
    950: '#080A2D', // Base
  },
  // Purple scale
  purple: {
    50: '#FBF5FE',
    100: '#F5E9FE',
    200: '#EDD2FC',
    300: '#E0AFF8',
    400: '#CF80F2',
    500: '#BE5DE8', // Base
    600: '#9E2FCA',
    700: '#8524A7',
    800: '#6F1F89',
    900: '#5E1F70',
    950: '#3B084A',
  },
  // Magenta scale
  magenta: {
    50: '#FDF2FA',
    100: '#FBE8F6',
    200: '#F9D1EF',
    300: '#F6ABE2',
    400: '#EF77CC',
    500: '#E54FB4', // Base
    600: '#D32F96',
    700: '#B71F7A',
    800: '#971D64',
    900: '#7E1D56',
    950: '#4D0A31',
  },
  // Red scale
  red: {
    50: '#FDF2F1',
    100: '#FAE0E0',
    200: '#F7C7C6',
    300: '#F2A2A0',
    400: '#FF3030', // Legacy - not aligned with design system, kept for backwards compatibility
    '400New': '#EE6E6A', // Design system value
    500: '#EC463D', // Base - Design system value
    600: '#DB3429',
    700: '#B92A1F',
    800: '#98231B',
    900: '#450B08', // Aligned with design system
    950: '#450B08',
  },
  // Orange scale
  orange: {
    50: '#FFF9EC', // Design system value
    100: '#FFF2D3', // Aligned with design system
    200: '#FFE1A6',
    300: '#FFCA6E',
    400: '#FFA733',
    500: '#FF8B0C', // Base - Aligned with design system
    600: '#ED6C02',
    700: '#CA5404', // Aligned with design system
    800: '#A0410C',
    900: '#81380D', // Aligned with design system
    950: '#461904',
  },
  // Yellow scale
  yellow: {
    50: '#FEFFE7',
    100: '#FFF9EC', // Legacy - not aligned with design system, kept for backwards compatibility
    '100New': '#FBFFC1', // Design system value
    200: '#FCFF86',
    300: '#FFFC41',
    400: '#FFEF0D',
    500: '#FFD704', // Legacy - not aligned with design system, kept for backwards compatibility
    '500New': '#FCDE00', // Design system value (Base)
    600: '#D1A600',
    700: '#A67702',
    800: '#895D0A',
    900: '#744C0F',
    950: '#442804',
  },
  // Soft Amber scale
  softAmber: {
    50: '#F7F5F2',
    100: '#EBE9E2',
    200: '#D7D1C4',
    300: '#C2B8A7', // Base
    400: '#A4927C',
    500: '#927D65',
    600: '#856E59',
    700: '#6C584B',
    800: '#574840',
    900: '#473B35',
    950: '#251F1D',
  },
  // Neutral Grey scale
  neutralGrey: {
    50: '#F6F6F6',
    100: '#E7E7E7',
    200: '#D1D1D1',
    300: '#B0B0B0',
    400: '#888888',
    500: '#6D6D6D', // Base
    600: '#5D5D5D',
    700: '#4F4F4F',
    800: '#454545',
    900: '#3D3D3D',
    950: '#1D1D1D',
  },
  // Midnight Grey scale
  midnightGrey: {
    50: '#F6F6F7',
    100: '#EEEFF1',
    200: '#E0E1E5',
    300: '#CCCDD5',
    400: '#B7B7C2',
    500: '#908E9D',
    600: '#9D9CAB', // Base
    700: '#7C7A88',
    800: '#66646F',
    900: '#55535C',
    950: '#313135',
  },
  // Cool Grey scale
  coolGrey: {
    50: '#F6F6F7',
    100: '#EEEFF1',
    200: '#E0E1E5',
    300: '#CCCDD5',
    400: '#B7B7C2',
    500: '#9D9CAB', // Base
    600: '#908E9D',
    700: '#7C7A88',
    800: '#66646F',
    900: '#55535C',
    950: '#313135',
  },
  // Beige scale (Tan, Ecru, Ivory)
  beige: {
    main: '#F3CF99', // Tan (Base)
    light: '#F3CF99', // Legacy - not aligned with design system, kept for backwards compatibility
    lightNew: '#F8F4EF', // Ivory - from design system
    dark: '#F3CF99', // Legacy - not aligned with design system, kept for backwards compatibility
    darkNew: '#F3E7D9', // Ecru - from design system
    contrastText: '#1D1D1D',
    50: '#F8F4EF', // Ivory
    100: '#F3E7D9', // Ecru
    300: '#F3CF99', // Tan
  },
  // Legacy amber (maintained for compatibility)
  amber: {
    primary: '#C2B8A7', // softAmber-300
    secondary: '#F7F5F2', // softAmber-50
    100: '#F2EFED', // Deprecated
    150: '#EBE9E2', // softAmber-100
    200: '#D7D1C4', // softAmber-200
    300: '#DFD8D2', // Deprecated
  },
  // Generic colors
  generic: {
    primary: '#FFFFFF', // White
    secondary: '#000000', // Black
  },
  black: {
    primary: '#000000',
    secondary: '#0000008A',
  },
  // Legacy categories (maintained for compatibility)
  tableRow: {
    primary: '#EBEBF0',
    secondary: '#FFF',
  },
  border: {
    primary: '#1D1D1D',
    secondary: 'rgba(8, 10, 45, 0.3)',
    light: '#00000014',
  },
  neutral: {
    50: '#FAFAFA',
    100: '#F6F6F6',
    400: '#D1D3DB',
  },
  indigo: {
    900: '#121768', // Legacy - consider using blue scale instead
  },
} satisfies PaletteOptions;

const riPalette = {
  text: {
    secondary: '#595959',
  },
} as const satisfies Pick<PaletteOptions, 'text'>;

export { palette as default, riPalette };
